import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FenceService } from '../../../../services/configurators-2d/applications/fence.service';
import { TranslateService } from '@ngx-translate/core';
import { Shape } from '../../../../models/configurators-2d/applications/fence/shape';
import { Side } from '../../../../models/configurators-2d/applications/fence/side';
import { Section } from 'src/app/models/configurators-2d/applications/fence/section';
import { FenceConfiguratorItem } from '../../configurator/applications/fence-configurator-item';
import { Height } from 'src/app/models/configurators-2d/applications/fence/height';
import { IBaseComponent } from '../../configurator/i-base-component';
import { Configuration } from '../../../../models/configurators-2d/applications/fence/configuration';
import { MODAL, ModalService } from '../../../../services/modal.service';
import { PanelCount } from 'src/app/models/configurators-2d/applications/fence/panel-count';
import { FenceConstants } from 'src/app/models/configurators-2d/applications/fence/fence-constants';

@Component({
  selector: 'ng-fence-fence-configuration',
  templateUrl: './configuration.component.html',
})
export class FenceConfigurationComponent extends FenceConfiguratorItem implements OnInit, IBaseComponent {
  /** URL de base des images */
  baseImgURL: string = undefined;

  /** Formes */
  shapes: Shape[];
  /** Côtés */
  sides: Side[];
  /** Hauteurs de poteaux */
  heights: Height[];

  /** Emission du changement de la sous étape */
  @Output() onSubStepChange: EventEmitter<number> = new EventEmitter<number>();

  /** Le côté débute sur un mur */
  wallStart: boolean;
  /** Le côté termine sur un mur */
  wallEnd: boolean;

  /** Sous-étape */
  subStep: number = 1;

  sideCount: number = 1;

  basePlateTooltip: string;

  constructor(
    fenceService: FenceService,
    private translate: TranslateService,
    private modalService: ModalService
  ) {
    super(fenceService);
    this.baseImgURL = this.service.apiUrl;
  }

  ngOnInit(): void {
    this.service.getShapes().then(shapes => {
      let freeFormShape = new Shape();
      freeFormShape.id = 0;
      freeFormShape.isFree = true;
      freeFormShape.nameFr = 'Forme libre';
      freeFormShape.nameEn = 'Free form';
      this.shapes = shapes.concat([freeFormShape]);
    });

    this.sides = this.configuration.sides;
    const heightLowWall = Number(this.configuration.wallHeight === undefined ? 0 : this.configuration.wallHeight);
    this.heights = this.configuration.location.heights.filter(h => h.height + heightLowWall < 243);

    this.basePlateTooltip = this.translate.instant('fence.configuration.base-plate-tooltip');
  }

  /**
   * Fonction d'affichage des sous menus
   * @param step sous étape de la page finition
   */
  toggleCollapse(step: number) {
    if (step === 1 && this.subStep === 0) {
      this.subStep = 1;
    } else if (step === 1 && this.subStep === 2) {
      this.subStep = 1;
    } else if (step === 2 && this.subStep === 0) {
      this.subStep = 2;
    } else if (step === 2 && this.subStep === 1) {
      this.subStep = 2;
    } else {
      this.subStep = 0;
    }
  }

  /**
   * Fonction de sélection de la forme
   * @param shape forme à sélectionner
   */
  selectShape(shape: Shape) {
    this.configuration.shape = shape;

    if (this.configuration.sides !== undefined && this.configuration.sides.length !== shape.sides.length) {
      //Si les côtés enregistrés sont différents de ceux sélectionnés, alors on les ajoute
      for (const element of shape.sides) {
        if (this.configuration.sides.filter((s: Side) => s.id === element).length <= 0) {
          this.configuration.sides.push({
            id: element,
            isActive: true,
            startOrEndOnWall: 0,
            nonRightAngle: false,
            sections: [],
            verticalDecorativeBoards: [],
            verticalDecorations: [],
            verticalPanels: [],
            panelCount: new PanelCount(),
          });
        }
      }

      //Si les côtés ne doivent plus être enregistrés on les supprime
      for (let i = 0; i < this.configuration.sides.length; i++) {
        if (!shape.sides.includes(this.configuration.sides[i].id)) {
          this.configuration.sides.splice(i);
        }
      }
    } else if (this.configuration.sides === undefined) {
      //Si l'objet côtés est vide, alors on initialise les côtés
      let sides: Side[] = [];
      for (const element of shape.sides) {
        sides.push({
          id: element,
          isActive: true,
          startOrEndOnWall: 0,
          nonRightAngle: false,
          sections: [],
          verticalDecorativeBoards: [],
          verticalDecorations: [],
          verticalPanels: [],
          panelCount: new PanelCount(),
        });
      }
      this.configuration.sides = sides;
    }

    //si la forme n'a pas d'angle, on réinitialise les données concernant les angles
    if (shape.sidesCount === 1){
      this.configuration.nbNonRightAngle = null;
      this.configuration.nbConcretePilarInAngle = null;
    }
  }

  /**
   * Fonction de détermination si la forme est sélectionnée ou non
   * @param shape
   * @returns boolean
   */
  isShapeSelected(shape: Shape) {
    return this.configuration.shape?.id === shape?.id;
  }

  /**
   * Fonction d'ajout d'un section
   * @param id ID du côté
   */
  addSection(id: string) {
    let indexSide = this.configuration.sides.findIndex((s: Side) => s.id === id);

    this.configuration.sides[indexSide].sections.push({
      length: 0,
      height: 0,
      hasDoor: false,
      hasNonRightAngle: false,
      hasBasePlates: false,
      wallStart: false,
      wallEnd: false,
    });
  }

  /**
   * Fonction de suppression d'une hauteur
   * @param sideId id du côté
   * @param id id de la section
   */
  deleteSection(sideId: string, id: number): void {
    let indexSide = this.configuration.sides.findIndex((s: Side) => s.id === sideId);
    this.configuration.sides[indexSide].sections.splice(id, 1);
    this.configuration.sides[indexSide].verticalDecorations = [];
  }

  /**
   * Fonction de duplication d'une hauteur
   * @param sideId id du côté
   * @param id section à dupliquer
   */
  duplicateSection(sideId: string, section: Section): void {
    let indexSide = this.configuration.sides.findIndex((s: Side) => s.id === sideId);

    this.configuration.sides[indexSide].sections.push({
      length: section.length,
      height: section.height,
      hasDoor: section.hasDoor,
      hasNonRightAngle: section.hasNonRightAngle,
      hasBasePlates: section.hasBasePlates,
      wallStart: section.wallStart,
      wallEnd: section.wallEnd,
    });
  }

  /**
   * Fonction de sélection du nombre de côtés pour la forme autre
   * @param event event
   */
  selectSideCount(event: any) {
    this.sideCount = parseInt(event.target.value);

    //Ajout des côtés qui ne sont pas dans la configuration
    for (let i = 0; i < this.sideCount; i++) {
      let letter: string = String.fromCharCode('A'.charCodeAt(0) + i);

      if (!this.configuration.sides.find((s: Side) => s.id === letter)) {
        this.configuration.sides.push({
          id: letter,
          isActive: true,
          startOrEndOnWall: 0,
          nonRightAngle: false,
          sections: [],
          verticalDecorativeBoards: [],
          verticalDecorations: [],
          verticalPanels: [],
          panelCount: new PanelCount(),
        });
      }
    }

    //Suppression des côtés en trop
    if (this.configuration.sides.length > this.sideCount) {
      this.configuration.sides.splice(this.sideCount, this.configuration.sides.length - this.sideCount);
    }
  }

  confirmAngle(hasAngle: boolean){
    if (!hasAngle){
      this.configuration.confirmNoAngle = true;
    } else {
      this.configuration.confirmNoAngle = false;
      this.modalService.display(MODAL.MESSAGE, {
        titleRef: 'popins.fence.title',
        contentRef: 'popins.fence.confirm-angle-warning',
        primary: true,
        buttons: [
          {
            labelRef: 'generic.ok',
            action: () => {
              this.modalService.close();
              //reset
              for (let i = 0; i < this.configuration.sides.length; i++) {
                this.configuration.sides.splice(i);
              }
              this.configuration.shape = undefined;
              this.configuration.confirmNoAngle = null;
            },
          },
        ],
      });
    }
  }

  isNextButtonActive(): boolean {
    let isSideUncompleted = false;
    let isSectionMissing = false;
    if (this.configuration.sides != null) {
      for (const side of this.configuration.sides) {
        if (side.sections.length === 0) {
          isSectionMissing = true;
        } else {
          for (const section of side.sections) {
            if (section.length === 0 || !section.height) {
              isSideUncompleted = true;
            }
          }
        }
      }
    }

    return this.configuration.shape && !isSideUncompleted && !isSectionMissing && (this.configuration.confirmNoAngle === true || this.configuration.shape.sidesCount !==1);
  }

  checkPageChangeCapability(): boolean {
    return this.configuration.location &&
    (
      !this.configuration.location.isOnWall ||
      (
        this.configuration.wallHeight > 0 &&
        this.configuration.wallHeight <= FenceConstants.MAX_WALL_HEIGHT
      )
    );
  }

  /**
   * Vérifie si la section peut avoir un portillon
   * @param section
   */
  sectionCanHaveDoors(section: Section): boolean {
    if (!section) {
      return false;
    }

    let height = this.heights.find(h => h.height == section.height);
    return height?.canHaveGates;
  }

  /**
   * Reset l'installation/poteau/lame/type de poteau/portillon/lame décor/décor/panneau quand on change la hauteur
   */
  resetPosts() {
    this.configuration.configurationInstallations = [];
    this.configuration.postType = undefined;
    this.configuration.horizontalDecorativeBoards = undefined;
    this.configuration.horizontalDecorations = undefined;
    this.configuration.horizontalPanels = undefined;
    this.configuration.doors = undefined;

    this.configuration.sides.forEach(s => {
      s.verticalDecorations = [];
      s.verticalPanels = [];
    });
  }

  /**
   * Change la valeur de la longueur en convertissant de mètre en centimètre
   * @param $event
   * @param section
   */
  changeLength($event: Event, section: Section) {
    // conversion m to cm
    section.length = Math.round(Number(($event.target as HTMLInputElement).value) * 100);
  }

  /**
   * Vérifie si la location utilise des poteaux scellés
   */
  isNotSealed() {
    let config: Configuration = this.configuration;
    return config?.location && !config.location.isSealed;
  }

  /**
   * Ouvre la modal avec les infos du kit d'angle
   */
  getInfosAngleKit() {
    this.modalService.display(MODAL.FENCE_INFOS_ANGLE_KIT);
  }

  /**
   * Ouvre la modal avec les infos de la plaque de soubassement
   */
  getInfosBasePlate() {
    this.modalService.display(MODAL.FENCE_INFOS_BASE_PLATE);
  }
}
